import { normalizeVariationName } from './variationNames.js';
import { normalizePromoCode } from './promoCode.js';

const API_BASE = 'https://api.elitefitness.it';

/**
 * Maps {@link PromoCodePublicDTO} from GET /api/promo/{code} to the shape used by checkout
 * (same fields as validate's promoCode, without owner / state internals).
 */
export function mapPublicPromoToCheckout(dto) {
    if (!dto || !dto.code) return null;
    return {
        id: dto.id,
        code: dto.code,
        discountAmount: dto.discountAmount,
        percentage: dto.percentage,
        startDate: dto.startDate,
        endDate: dto.endDate,
        active: dto.active,
        firstOrder: dto.firstOrder,
        existingMembersOnly: dto.existingMembersOnly,
        maxUsesPerUser: dto.maxUsesPerUser,
        accessPolicy: dto.accessPolicy,
        variations: dto.variations || {},
        freeDaysPerProduct: dto.freeDaysPerProduct || {},
        freeDaysPerProductVariation: dto.freeDaysPerProductVariation || {},
        upsellsDiscount: dto.upsellsDiscount,
        // Interruttore dello staff (devops#427): senza copiarlo la regola del client non lo vede nel percorso ospite.
        coversInsuranceAndMembership: dto.coversInsuranceAndMembership,
        filters: {
            productIds: dto.productIds || [],
            centerIds: dto.centerIds || [],
        },
    };
}

/** Normalized variation name → server price row from GET /api/promo/{code}/variations */
export function indexPromoVariationPrices(rows) {
    const index = {};
    for (const row of rows || []) {
        const key = normalizeVariationName(row?.variationName);
        if (!key) continue;
        index[key] = {
            variationName: row.variationName,
            price: Number(row.price),
            discountedPrice: Number(row.discountedPrice),
        };
    }
    return index;
}

export function promoCoversProduct(promo, productId) {
    if (!promo || !productId) return false;
    const ids = promo.filters?.productIds ?? promo.productIds ?? [];
    if (!Array.isArray(ids) || ids.length === 0) return true;
    return ids.map(String).includes(String(productId));
}

/**
 * Guest checkout preview: public promo metadata + discounted variation prices (no auth).
 * @returns {{ ok: true, promo: object, variationPrices: object } | { ok: false, status?: number }}
 */
export async function fetchGuestPromoPreview(code, productId) {
    const sanitized = normalizePromoCode(code);
    if (!sanitized || !productId) {
        return { ok: false };
    }
    const enc = encodeURIComponent(sanitized);
    const promoResp = await fetch(`${API_BASE}/api/promo/${enc}`);
    if (!promoResp.ok) {
        return { ok: false, status: promoResp.status };
    }
    const promoDto = await promoResp.json();
    const promo = mapPublicPromoToCheckout(promoDto);
    if (!promo || !promoCoversProduct(promo, productId)) {
        return { ok: false, status: 404 };
    }
    const varResp = await fetch(
        `${API_BASE}/api/promo/${enc}/variations?productId=${encodeURIComponent(productId)}`
    );
    if (!varResp.ok) {
        return { ok: false, status: varResp.status };
    }
    const variationRows = await varResp.json();
    return {
        ok: true,
        promo,
        variationPrices: indexPromoVariationPrices(variationRows),
        variationRows: Array.isArray(variationRows) ? variationRows : [],
    };
}
